import { Popover as BasePopover } from '@base-ui/react/popover';
import { RiCloseLine, RiDraggable } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { type IconLike } from './icon';
import { IconChip } from './icon-chip';
import { type ChipColor } from './lib/colors';
import { usePortalContainer, type PortalContainer } from './lib/portal';
import { popupMotion } from './lib/surfaces';

/* ================================================================================================
 * Glass panel chrome — "Joylashtirish popover" 382:6582 / widget popovers 401:8934 (spec §6.10,
 * 02-constructor §2.3): `glass-popover` r12, w300; drag bar h40 (draggable 16 + close 16, both `#35b5ec`);
 * title row h52 (chip 28 r8 + 600 15/auto); body padding 18/16, gap 18; footer padding 16, gap 10.
 * ============================================================================================== */

export interface PopoverChromeProps {
  title?: React.ReactNode;
  icon?: { icon: IconLike; color?: ChipColor };
  /** Show the drag bar; spread `dragHandleProps` on it to make the panel draggable (pointer handlers). */
  draggable?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  /** Close button in the drag bar (or the title row when there is no drag bar). */
  onClose?: () => void;
  closeLabel?: string;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  bodyClassName?: string;
}

const Divider = () => <div aria-hidden className="h-px shrink-0 bg-line" />;

function CloseButton({ onClick, label, render }: { onClick?: () => void; label: string; render?: (button: React.ReactElement) => React.ReactNode }) {
  const button = (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      // 20 px hit area around the 16 px icon; -mr-2 keeps the icon itself 16 px from the panel edge as in every
      // design (401:8934, 423:9143 …: close-line 17 px from the outer edge incl. the stroke)
      className="-mr-2 flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-4 text-accent-2 transition-colors hover:bg-hover"
    >
      <RiCloseLine aria-hidden className="size-16" />
    </button>
  );
  return render ? render(button) : button;
}

function ChromeContent({
  title,
  icon,
  draggable,
  dragHandleProps,
  onClose,
  closeLabel = 'Yopish',
  footer,
  children,
  bodyClassName,
  renderClose,
  TitleTag = 'h2',
}: PopoverChromeProps & { renderClose?: (button: React.ReactElement) => React.ReactNode; TitleTag?: React.ElementType }) {
  const showClose = Boolean(onClose || renderClose);
  return (
    <>
      {draggable && (
        <>
          <div
            {...dragHandleProps}
            className={cn('flex h-40 shrink-0 items-center justify-between gap-8 px-16', dragHandleProps?.className, 'cursor-grab touch-none active:cursor-grabbing')}
          >
            <RiDraggable aria-hidden className="size-16 text-accent-2" />
            {showClose && <CloseButton onClick={onClose} label={closeLabel} render={renderClose} />}
          </div>
          <Divider />
        </>
      )}
      {(title || icon) && (
        <>
          <div className="flex h-52 shrink-0 items-center gap-10 px-16">
            {icon && <IconChip icon={icon.icon} color={icon.color ?? 'darkBlue'} size={28} />}
            <TitleTag className="min-w-0 flex-1 truncate text-15 leading-auto font-semibold text-fg">{title}</TitleTag>
            {!draggable && showClose && <CloseButton onClick={onClose} label={closeLabel} render={renderClose} />}
          </div>
          <Divider />
        </>
      )}
      <div className={cn('flex min-h-0 flex-col gap-18 overflow-y-auto px-16 py-18', bodyClassName)}>{children}</div>
      {footer && <div className="flex shrink-0 items-center justify-end gap-10 p-16">{footer}</div>}
    </>
  );
}

export interface PopoverPanelProps extends PopoverChromeProps, Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'draggable'> {
  /** Width in design px. Default 300. */
  width?: number;
}

/**
 * Static glass panel (no anchoring) — the floating constructor panels (widget settings, 3D placement) that are
 * positioned and dragged by the editor.
 */
export function PopoverPanel({
  width = 300,
  title,
  icon,
  draggable,
  dragHandleProps,
  onClose,
  closeLabel,
  footer,
  bodyClassName,
  className,
  style,
  children,
  ...rest
}: PopoverPanelProps) {
  return (
    <section
      aria-label={typeof title === 'string' ? title : undefined}
      {...rest}
      style={{ width: `${width / 16}rem`, ...style }}
      className={cx('glass-popover flex max-h-full flex-col overflow-hidden rounded-12 text-fg', className)}
    >
      <ChromeContent
        title={title}
        icon={icon}
        draggable={draggable}
        dragHandleProps={dragHandleProps}
        onClose={onClose}
        closeLabel={closeLabel}
        footer={footer}
        bodyClassName={bodyClassName}
      >
        {children}
      </ChromeContent>
    </section>
  );
}

export interface PopoverProps extends Omit<PopoverChromeProps, 'onClose' | 'dragHandleProps'> {
  /** The anchor/trigger element (Button, IconButton…). */
  trigger: React.ReactElement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** Distance from the trigger in CSS px. Default 8. */
  sideOffset?: number;
  /** Width in design px (default 300). `auto` hugs the content. */
  width?: number | 'auto';
  /** Show the × close button. Default: true when there is a title or drag bar. */
  showClose?: boolean;
  closeLabel?: string;
  /** Open on hover as well (info-tips). */
  openOnHover?: boolean;
  /** Plain content without the chrome padding (menus of custom content). */
  bare?: boolean;
  /**
   * `popover` (default) — `glass-popover` (382:6582); `tooltip` — the lighter hover-card glass `glass-tooltip`
   * (White/10 over #474747/20, White/10 stroke, blur 10, no shadow — "Kirish huquqi" 662:17179).
   */
  surface?: 'popover' | 'tooltip';
  className?: string;
  container?: PortalContainer;
}

/** Anchored glass popover (Base UI Popover). */
export function Popover({
  trigger,
  open,
  defaultOpen,
  onOpenChange,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  width = 300,
  showClose,
  closeLabel = 'Yopish',
  openOnHover,
  bare,
  surface = 'popover',
  title,
  icon,
  draggable,
  footer,
  children,
  bodyClassName,
  className,
  container,
}: PopoverProps) {
  const portalContainer = usePortalContainer(container);
  const withClose = showClose ?? Boolean(title || draggable);
  return (
    <BasePopover.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}>
      <BasePopover.Trigger render={trigger} openOnHover={openOnHover} />
      <BasePopover.Portal container={portalContainer}>
        <BasePopover.Positioner side={side} align={align} sideOffset={sideOffset} collisionPadding={8} className="z-75">
          <BasePopover.Popup
            style={width === 'auto' ? undefined : { width: `${width / 16}rem` }}
            className={cx(
              surface === 'tooltip' ? 'glass-tooltip' : 'glass-popover',
              'flex max-h-(--available-height) flex-col overflow-hidden rounded-12 text-fg outline-none',
              popupMotion,
              className,
            )}
          >
            {bare ? (
              children
            ) : (
              <ChromeContent
                title={title}
                icon={icon}
                draggable={draggable}
                footer={footer}
                bodyClassName={bodyClassName}
                closeLabel={closeLabel}
                TitleTag={BasePopover.Title}
                renderClose={withClose ? (button) => <BasePopover.Close render={button} /> : undefined}
              >
                {children}
              </ChromeContent>
            )}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
